import React, { useEffect, useState } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import { mdiMagnify, mdiCloseCircleOutline } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import LoadingWidget from "../../../../context_providers/modal/loader";
import {
  fetchGet,
  fetchPost,
} from "../../content_panel/services/serviceClient";
import WarehouseSearchFieldByNameID from "../common-components/WarehouseSearchFieldByNameID";

const BASE = `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v3/erp/inventory/faulty-resolution`;

interface FaultyItem {
  faulty_item_code: string;
  working_item_code: string;
  item_name: string;
  is_device_based: boolean;
  faulty_units: number;
  warehouse_id: string;
}

interface DeviceData {
  is_device_based: boolean;
  device_ids: string[];
  available_qty: number;
}

interface HistoryRow {
  id: string;
  date: string;
  action: "scrap" | "restore";
  faulty_item_code: string;
  working_item_code: string;
  warehouse_id: string;
  device_ids: string[];
  quantity: number;
  remark: string;
  disposition_id: string;
  user_name: string;
  user_id: string;
}

const FaultyResolution = () => {
  const { showToast } = useToast();

  const [warehouseId, setWarehouseId] = useState("");
  const [warehouseName, setWarehouseName] = useState("");
  const [items, setItems] = useState<FaultyItem[]>([]);
  const [itemSearch, setItemSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [selected, setSelected] = useState<FaultyItem | null>(null);
  const [deviceData, setDeviceData] = useState<DeviceData | null>(null);
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);
  const [quantity, setQuantity] = useState<number>(0);
  const [remark, setRemark] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const [tab, setTab] = useState<"resolve" | "history">("resolve");
  const [history, setHistory] = useState<HistoryRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyAction, setHistoryAction] = useState<
    "all" | "scrap" | "restore"
  >("all");
  const [historySearch, setHistorySearch] = useState("");
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [searchTrigger, setSearchTrigger] = useState(0);

  const resetSelection = () => {
    setSelected(null);
    setDeviceData(null);
    setSelectedDevices([]);
    setQuantity(0);
    setRemark("");
  };

  const fetchItems = async (whId: string) => {
    if (!whId) return;
    try {
      setLoading(true);
      resetSelection();
      const res = await fetchGet(`${BASE}/items?warehouse_id=${whId}`);
      if (res.success) {
        setItems(res.data || []);
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Failed to load faulty items",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to load faulty items",
      });
    } finally {
      setLoading(false);
    }
  };

  const openItem = async (item: FaultyItem) => {
    try {
      setSelected(item);
      setSelectedDevices([]);
      setQuantity(0);
      setRemark("");
      setDeviceData(null);
      const res = await fetchGet(
        `${BASE}/devices?faulty_item_code=${encodeURIComponent(item.faulty_item_code)}&warehouse_id=${warehouseId}`,
      );
      if (res.success) {
        setDeviceData(res.data);
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Failed to load devices",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to load devices",
      });
    }
  };

  const toggleDevice = (id: string) => {
    setSelectedDevices((prev) =>
      prev.includes(id) ? prev.filter((d) => d !== id) : [...prev, id],
    );
  };

  const doAction = async (action: "scrap" | "restore") => {
    if (!selected) return;
    const isDevice = selected.is_device_based;

    if (isDevice && selectedDevices.length === 0) {
      showToast({ type: ToastType.error, text: "Select at least one device" });
      return;
    }
    if (!isDevice && (!quantity || quantity <= 0)) {
      showToast({
        type: ToastType.error,
        text: "Enter a quantity greater than 0",
      });
      return;
    }
    if (!isDevice && deviceData && quantity > deviceData.available_qty) {
      showToast({
        type: ToastType.error,
        text: `Quantity cannot exceed available faulty stock (${deviceData.available_qty})`,
      });
      return;
    }

    try {
      setActionLoading(true);
      const body: any = {
        faulty_item_code: selected.faulty_item_code,
        warehouse_id: warehouseId,
        ...(action === "scrap" ? { reason: remark } : { note: remark }),
      };
      if (isDevice) body.device_ids = selectedDevices;
      else body.quantity = quantity;

      const res = await fetchPost(`${BASE}/${action}`, body);
      if (res.success) {
        showToast({
          type: ToastType.success,
          text:
            action === "scrap"
              ? "Scrapped successfully"
              : "Restored to working stock successfully",
        });
        await fetchItems(warehouseId);
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || `Failed to ${action}`,
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || `Failed to ${action}`,
      });
    } finally {
      setActionLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const params = new URLSearchParams({
        page: String(historyPage),
        limit: "10",
      });
      if (warehouseId) params.set("warehouse_id", warehouseId);
      if (historyAction !== "all") params.set("action", historyAction);
      if (historySearch.trim()) params.set("search", historySearch.trim());

      const res = await fetchGet(`${BASE}/history?${params.toString()}`);
      if (res.success) {
        setHistory(res.data || []);
        setHistoryTotalPages(res.totalPages || 1);
      } else {
        showToast({
          type: ToastType.error,
          text: res.error || "Failed to load history",
        });
      }
    } catch (e: any) {
      showToast({
        type: ToastType.error,
        text: e?.message || "Failed to load history",
      });
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (tab === "history") fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, historyPage, historyAction, warehouseId, searchTrigger]);

  const triggerHistorySearch = () => {
    setHistoryPage(1);
    setSearchTrigger((x) => x + 1);
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return dateStr;
    return d.toLocaleString();
  };

  const term = itemSearch.trim().toLowerCase();
  const filteredItems = term
    ? items.filter(
        (it) =>
          it.faulty_item_code.toLowerCase().includes(term) ||
          it.working_item_code.toLowerCase().includes(term) ||
          (it.item_name || "").toLowerCase().includes(term),
      )
    : items;

  return (
    <main
      id="warehouse-inventory-faulty-resolution-page"
      className="p-3 md:p-6 animate-fade-in space-y-4 md:space-y-6"
    >
      {(loading || actionLoading) && <LoadingWidget />}

      <div className="flex flex-wrap justify-between items-center gap-3">
        <h1 className="text-lg sm:text-2xl md:text-3xl font-bold border-l-4 border-myPrimaryColor pl-2">
          Faulty Device Resolution
        </h1>
        <div className="flex gap-1 rounded-lg border bg-gray-50 p-1">
          <button
            id="warehouse-inventory-faulty-resolve-tab-btn"
            className={`px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium ${
              tab === "resolve"
                ? "bg-white shadow text-primary"
                : "text-muted-foreground"
            }`}
            onClick={() => setTab("resolve")}
          >
            Resolve
          </button>
          <button
            id="warehouse-inventory-faulty-history-tab-btn"
            className={`px-3 sm:px-4 py-1.5 rounded-md text-xs sm:text-sm font-medium ${
              tab === "history"
                ? "bg-white shadow text-primary"
                : "text-muted-foreground"
            }`}
            onClick={() => setTab("history")}
          >
            History
          </button>
        </div>
      </div>

      <div className="rounded-lg border bg-white p-3 md:p-4 relative z-20 overflow-visible">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div className="w-full">
            <WarehouseSearchFieldByNameID
              placeholder="Filter by Warehouse"
              value={warehouseName}
              onChange={setWarehouseName}
              onClear={() => {
                setWarehouseName("");
                setWarehouseId("");
                setItems([]);
                setItemSearch("");
                resetSelection();
              }}
              onSelect={(wh: any) => {
                setWarehouseId(wh.id);
                setWarehouseName(wh.name);
                setItemSearch("");
                fetchItems(wh.id);
              }}
            />
          </div>
          {tab === "resolve" && (
            <div className="relative w-full">
              <Icon
                path={mdiMagnify}
                className="absolute left-3 top-1/3 -translate-y-1/2 h-5 w-5 text-muted-foreground"
              />
              <input
                id="warehouse-inventory-faulty-item-search-input"
                className="h-10 w-full rounded-md border px-3 pl-9 pr-9 text-sm disabled:bg-gray-50"
                placeholder="Search by item code or name..."
                value={itemSearch}
                // disabled={!warehouseId}
                onChange={(e) => setItemSearch(e.target.value)}
              />
              {itemSearch && (
                <button
                  id="warehouse-inventory-faulty-item-search-clear-btn"
                  onClick={() => setItemSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"
                  title="Clear search"
                >
                  <Icon path={mdiCloseCircleOutline} size={0.7} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {tab === "resolve" && warehouseId && (
        <div className="rounded-lg border shadow-sm bg-white">
          <div className="p-4 md:p-6 pb-2 md:pb-3">
            <h3 className="text-sm md:text-base font-bold">Faulty Stock</h3>
          </div>

          {/* Desktop / tablet: table */}
          <div className="hidden md:block p-6 pt-0 overflow-x-auto">
            <table className="w-full min-w-max text-sm">
              <thead className="border-b">
                <tr>
                  <th className="p-3 text-left text-muted-foreground">
                    Faulty Code
                  </th>
                  <th className="p-3 text-left text-muted-foreground">
                    Working Code
                  </th>
                  <th className="p-3 text-left text-muted-foreground">
                    Item Name
                  </th>
                  <th className="p-3 text-center text-muted-foreground">
                    Type
                  </th>
                  <th className="p-3 text-center text-muted-foreground">
                    Faulty Units
                  </th>
                  <th className="p-3 text-center text-muted-foreground">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 && (
                  <tr>
                    <td className="p-3 text-muted-foreground" colSpan={6}>
                      {items.length === 0
                        ? "No faulty stock in this warehouse."
                        : "No faulty items match your search."}
                    </td>
                  </tr>
                )}
                {filteredItems.map((it) => (
                  <tr
                    key={it.faulty_item_code}
                    className="border-b hover:bg-gray-50"
                  >
                    <td className="p-3 font-bold">{it.faulty_item_code}</td>
                    <td className="p-3">{it.working_item_code}</td>
                    <td className="p-3">{it.item_name}</td>
                    <td className="p-3 text-center">
                      {it.is_device_based ? "Device" : "Quantity"}
                    </td>
                    <td className="p-3 text-center text-red-600 font-bold">
                      {it.faulty_units}
                    </td>
                    <td className="p-3 text-center">
                      <button
                        id={`warehouse-inventory-faulty-manage-btn-${it.faulty_item_code}`}
                        className="px-3 py-1 rounded border text-primary font-medium hover:bg-gray-100"
                        onClick={() => openItem(it)}
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile: cards */}
          <div className="md:hidden p-3 pt-0 space-y-3">
            {filteredItems.length === 0 ? (
              <p className="text-xs text-muted-foreground py-2">
                {items.length === 0
                  ? "No faulty stock in this warehouse."
                  : "No faulty items match your search."}
              </p>
            ) : (
              filteredItems.map((it) => (
                <div
                  key={it.faulty_item_code}
                  id={`warehouse-inventory-faulty-item-card-${it.faulty_item_code}`}
                  className="rounded-lg border p-3 text-xs space-y-3"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span
                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                        it.is_device_based
                          ? "bg-blue-100 text-blue-700"
                          : "bg-purple-100 text-purple-700"
                      }`}
                    >
                      {it.is_device_based ? "Finished Goods" : "Raw Material"}
                    </span>
                    <span className="text-red-600 font-bold text-sm">
                      {it.faulty_units} faulty
                    </span>
                  </div>

                  <div>
                    <p className="text-[11px] text-muted-foreground">
                      Faulty Item Code
                    </p>
                    <p className="font-semibold text-sm break-all text-muted-foreground">
                      {it.faulty_item_code}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">
                      Working Item Code
                    </p>
                    <p className="font-semibold text-sm break-all text-muted-foreground">
                      {it.working_item_code || "-"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[11px] text-muted-foreground">
                      Item Name
                    </p>
                    <p className="font-semibold text-sm break-words text-muted-foreground">
                      {it.item_name || "-"}
                    </p>
                  </div>

                  <button
                    id={`warehouse-inventory-faulty-manage-btn-mobile-${it.faulty_item_code}`}
                    className="w-full px-3 py-2 rounded border text-primary font-medium hover:bg-gray-100"
                    onClick={() => openItem(it)}
                  >
                    Manage
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {tab === "resolve" && selected && deviceData && (
        <div
          id="warehouse-inventory-faulty-manage-panel"
          className="rounded-lg border shadow-sm bg-white p-4 md:p-6 space-y-4"
        >
          <div className="flex items-start justify-between gap-2">
            <h3 className="text-sm md:text-base font-bold break-all">
              Manage {selected.faulty_item_code}
            </h3>
            <button
              id="warehouse-inventory-faulty-manage-close-btn"
              className="shrink-0 text-xs md:text-sm text-muted-foreground hover:underline"
              onClick={resetSelection}
            >
              Close
            </button>
          </div>

          {deviceData.is_device_based ? (
            <div>
              <label className="text-xs md:text-sm font-medium">
                Select devices ({selectedDevices.length}/
                {deviceData.device_ids.length})
              </label>
              <div className="mt-2 max-h-60 overflow-y-auto border rounded p-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
                {deviceData.device_ids.length === 0 && (
                  <span className="text-muted-foreground text-sm">
                    No available devices.
                  </span>
                )}
                {deviceData.device_ids.map((id) => (
                  <label
                    key={id}
                    className="flex items-center gap-2 text-xs md:text-sm break-all"
                  >
                    <input
                      id={`warehouse-inventory-faulty-device-checkbox-${id}`}
                      type="checkbox"
                      className="shrink-0"
                      checked={selectedDevices.includes(id)}
                      onChange={() => toggleDevice(id)}
                    />
                    {id}
                  </label>
                ))}
              </div>
            </div>
          ) : (
            <div className="w-full sm:max-w-xs">
              <label className="text-xs md:text-sm font-medium">
                Quantity (available: {deviceData.available_qty})
              </label>
              <input
                id="warehouse-inventory-faulty-quantity-input"
                type="number"
                min={1}
                max={deviceData.available_qty}
                value={quantity || ""}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  if (Number.isNaN(val) || val <= 0) {
                    setQuantity(0);
                    return;
                  }
                  setQuantity(Math.min(val, deviceData.available_qty));
                }}
                disabled={deviceData.available_qty <= 0}
                className="mt-1 w-full border rounded p-2 text-sm disabled:bg-gray-50"
              />
              {deviceData.available_qty <= 0 && (
                <span className="text-xs text-red-600">
                  No faulty stock available to resolve.
                </span>
              )}
            </div>
          )}

          <div className="w-full sm:max-w-md">
            <label className="text-xs md:text-sm font-medium">
              Reason / Note
            </label>
            <input
              id="warehouse-inventory-faulty-remark-input"
              type="text"
              value={remark}
              onChange={(e) => setRemark(e.target.value)}
              className="mt-1 w-full border rounded p-2 text-xs md:text-sm"
              placeholder="Optional"
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              id="warehouse-inventory-faulty-scrap-btn"
              className="w-full sm:w-auto px-4 py-2 rounded bg-red-600 text-white text-sm font-medium disabled:opacity-50"
              disabled={actionLoading}
              onClick={() => doAction("scrap")}
            >
              Scrap
            </button>
            <button
              id="warehouse-inventory-faulty-restore-btn"
              className="w-full sm:w-auto px-4 py-2 rounded bg-green-600 text-white text-sm font-medium disabled:opacity-50"
              disabled={actionLoading}
              onClick={() => doAction("restore")}
            >
              Restore to Working
            </button>
          </div>
        </div>
      )}

      {tab === "history" && (
        <div className="rounded-lg border shadow-sm bg-white">
          <div className="p-4 md:p-6 pb-2 md:pb-3 flex flex-col md:flex-row md:flex-wrap md:items-center md:justify-between gap-3">
            <h3 className="text-sm md:text-base font-bold">
              Resolution History
            </h3>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 w-full md:w-auto">
              <select
                id="warehouse-inventory-history-action-select"
                className="h-10 w-full sm:w-auto rounded-md border px-3 text-xs md:text-sm"
                value={historyAction}
                onChange={(e) => {
                  setHistoryAction(e.target.value as any);
                  setHistoryPage(1);
                }}
              >
                <option value="all">All actions</option>
                <option value="scrap">Scrapped</option>
                <option value="restore">Restored</option>
              </select>
              <input
                id="warehouse-inventory-history-search-input"
                className="h-10 w-full sm:w-56 rounded-md border px-3 text-xs md:text-sm"
                placeholder="Search item / device / user..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") triggerHistorySearch();
                }}
              />
              <button
                id="warehouse-inventory-history-search-btn"
                className="h-10 w-full sm:w-auto px-4 rounded-md border text-xs md:text-sm font-medium hover:bg-gray-100"
                onClick={triggerHistorySearch}
              >
                Search
              </button>
            </div>
          </div>
          <div className="p-4 md:p-6 pt-0 md:pt-0">
            {!warehouseId && (
              <p className="text-xs text-muted-foreground mb-3">
                Showing history across all warehouses. Select a warehouse above
                to narrow it down.
              </p>
            )}

            {/* Desktop / tablet: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-max text-sm">
                <thead className="border-b">
                  <tr>
                    <th className="p-3 text-left text-muted-foreground">
                      Date
                    </th>
                    <th className="p-3 text-left text-muted-foreground">
                      Action
                    </th>
                    <th className="p-3 text-left text-muted-foreground">
                      Faulty Code
                    </th>
                    <th className="p-3 text-left text-muted-foreground">
                      Working Code
                    </th>
                    <th className="p-3 text-center text-muted-foreground">
                      Qty
                    </th>
                    <th className="p-3 text-left text-muted-foreground">
                      Device IDs
                    </th>
                    <th className="p-3 text-left text-muted-foreground">
                      Reason / Note
                    </th>
                    <th className="p-3 text-left text-muted-foreground">
                      User
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {historyLoading ? (
                    <tr>
                      <td className="p-3 text-muted-foreground" colSpan={8}>
                        Loading...
                      </td>
                    </tr>
                  ) : history.length === 0 ? (
                    <tr>
                      <td className="p-3 text-muted-foreground" colSpan={8}>
                        No resolution history found.
                      </td>
                    </tr>
                  ) : (
                    history.map((h) => (
                      <tr
                        key={h.id}
                        className="border-b align-top hover:bg-gray-50"
                      >
                        <td className="p-3 whitespace-nowrap">
                          {formatDateTime(h.date)}
                        </td>
                        <td className="p-3">
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-medium ${
                              h.action === "scrap"
                                ? "bg-red-100 text-red-700"
                                : "bg-green-100 text-green-700"
                            }`}
                          >
                            {h.action === "scrap" ? "Scrapped" : "Restored"}
                          </span>
                        </td>
                        <td className="p-3 font-bold">{h.faulty_item_code}</td>
                        <td className="p-3">{h.working_item_code || "-"}</td>
                        <td className="p-3 text-center">{h.quantity}</td>
                        <td className="p-3 max-w-xs">
                          {h.device_ids && h.device_ids.length > 0 ? (
                            <span className="break-words text-xs">
                              {h.device_ids.join(", ")}
                            </span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="p-3">{h.remark || "-"}</td>
                        <td className="p-3 whitespace-nowrap">
                          {h.user_name || "-"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Mobile: cards */}
            <div className="md:hidden space-y-3">
              {historyLoading ? (
                <p className="text-xs text-muted-foreground py-2">Loading...</p>
              ) : history.length === 0 ? (
                <p className="text-xs text-muted-foreground py-2">
                  No resolution history found.
                </p>
              ) : (
                history.map((h) => (
                  <div
                    key={h.id}
                    className="rounded-lg border p-3 text-xs space-y-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                          h.action === "scrap"
                            ? "bg-red-100 text-red-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {h.action === "scrap" ? "Scrapped" : "Restored"}
                      </span>
                      <span className="text-muted-foreground">
                        {formatDateTime(h.date)}
                      </span>
                    </div>

                    <div>
                      <p className="text-[11px] text-muted-foreground">
                        Faulty Item Code
                      </p>
                      <p className="font-bold text-sm break-all">
                        {h.faulty_item_code}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">
                        Working Item Code
                      </p>
                      <p className="font-bold text-sm break-all">
                        {h.working_item_code || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">
                        Quantity
                      </p>
                      <p className="font-bold text-sm">{h.quantity}</p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">
                        Device IDs
                      </p>
                      <p className="font-bold text-sm break-words">
                        {h.device_ids && h.device_ids.length > 0
                          ? h.device_ids.join(", ")
                          : "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">
                        Reason / Note
                      </p>
                      <p className="font-bold text-sm break-words">
                        {h.remark || "-"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[11px] text-muted-foreground">User</p>
                      <p className="font-bold text-sm break-words">
                        {h.user_name || "-"}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-between border-t pt-4 mt-3 text-xs md:text-sm">
              <span className="text-muted-foreground">
                Page {historyPage} of {historyTotalPages}
              </span>
              <div className="flex gap-2">
                <button
                  id="warehouse-inventory-history-prev-btn"
                  className="px-3 py-1 rounded border disabled:opacity-40"
                  disabled={historyPage <= 1}
                  onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                >
                  Prev
                </button>
                <button
                  id="warehouse-inventory-history-next-btn"
                  className="px-3 py-1 rounded border disabled:opacity-40"
                  disabled={historyPage >= historyTotalPages}
                  onClick={() => setHistoryPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default FaultyResolution;
